import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../utils/cn";

interface DateRange {
  start: string | undefined;
  end: string | undefined;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  maxDate?: string;
  minDate?: string;
  className?: string;
  label?: string;
  variant?: "standard" | "premium";
  /** Mostra um asterisco vermelho ao lado do label, indicando campo obrigatório. */
  required?: boolean;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  maxDate,
  minDate,
  className,
  label,
  variant = "standard",
  required,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropDirection, setDropDirection] = useState<"down" | "up">("down");
  const [viewDate, setViewDate] = useState(new Date());
  const [tempRange, setTempRange] = useState<DateRange>(value);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sincroniza estado temporário se o valor externo mudar (ex: limpar filtros)
  useEffect(() => {
    setTempRange(value);
  }, [value]);

  // Converte strings YYYY-MM-DD para objetos Date com segurança
  const startDate = useMemo(
    () =>
      tempRange.start ? new Date(tempRange.start + "T12:00:00") : undefined,
    [tempRange.start],
  );
  const endDate = useMemo(
    () => (tempRange.end ? new Date(tempRange.end + "T12:00:00") : undefined),
    [tempRange.end],
  );
  const limitDate = useMemo(
    () => (maxDate ? new Date(maxDate + "T12:00:00") : undefined),
    [maxDate],
  );
  const startLimitDate = useMemo(
    () => (minDate ? new Date(minDate + "T12:00:00") : undefined),
    [minDate],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setTempRange(value); // Restaura o valor real se fechar sem completar
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value]);

  // Calculate drop direction when opening
  const handleToggleOpen = () => {
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      // Estimated calendar height (premium ~430px, standard ~340px) + Action Bar ~68px
      const calendarHeight = variant === "premium" ? 430 : 340;
      const actionBarHeight = 68;
      const spaceBelow = window.innerHeight - rect.bottom - actionBarHeight;
      const spaceAbove = rect.top;
      if (spaceBelow < calendarHeight && spaceAbove > calendarHeight) {
        setDropDirection("up");
      } else {
        setDropDirection("down");
      }
    }
    setIsOpen((prev) => !prev);
  };

  const formatDateStr = (date: Date) => date.toISOString().split("T")[0];

  const handleDateClick = (date: Date) => {
    if (limitDate && date > limitDate) return;
    if (startLimitDate && date < startLimitDate) return;

    if (!tempRange.start || (tempRange.start && tempRange.end)) {
      // Primeiro clique ou reinício - Apenas visual, não dispara onChange (filtro global)
      setTempRange({ start: formatDateStr(date), end: undefined });
    } else {
      // Segundo clique - Completa o range e dispara o filtro global
      const start = new Date(tempRange.start + "T12:00:00");
      let finalRange: DateRange;

      if (date < start) {
        finalRange = { start: formatDateStr(date), end: tempRange.start };
      } else {
        finalRange = { start: tempRange.start, end: formatDateStr(date) };
      }

      setTempRange(finalRange);
      onChange(finalRange);
      setIsOpen(false);
    }
  };

  const renderCalendar = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          className={variant === "premium" ? "h-9 w-9" : "h-8 w-8"}
        />,
      );
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d, 12, 0, 0);
      const dateStr = formatDateStr(date);
      const isSelected =
        dateStr === tempRange.start || dateStr === tempRange.end;
      const isInRange =
        startDate && endDate && date > startDate && date < endDate;
      const isToday = formatDateStr(new Date()) === dateStr;
      const isDisabled =
        (limitDate && date > limitDate) ||
        (startLimitDate && date < startLimitDate);

      days.push(
        <button
          key={d}
          type="button"
          disabled={isDisabled}
          onClick={() => handleDateClick(date)}
          className={cn(
            variant === "premium" ? "h-9 w-9" : "h-8 w-8",
            "flex items-center justify-center text-[11px] font-bold rounded-xl transition-all relative",
            isToday &&
              !isSelected &&
              "text-purple-600 border border-purple-200 bg-purple-50/50",
            isSelected &&
              "bg-purple-600 text-white shadow-md shadow-purple-500/30 z-10",
            isInRange &&
              "bg-purple-100 text-purple-800 rounded-none first:rounded-l-xl last:rounded-r-xl",
            !isSelected &&
              !isInRange &&
              !isDisabled &&
              "hover:bg-gray-100 text-gray-800",
            isDisabled && "text-gray-300 cursor-not-allowed bg-gray-50/50",
          )}
        >
          <span className="relative z-10">{d}</span>
          {isInRange && <div className="absolute inset-0 bg-purple-100 z-0" />}
        </button>,
      );
    }
    return days;
  };

  const monthNames = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];

  return (
    <div className={cn("space-y-1.5 relative", className)} ref={containerRef}>
      {label && (
        <label className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-gray-700">
          <span>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </span>
        </label>
      )}
      <div
        onClick={handleToggleOpen}
        className="w-full flex items-center justify-between transition-all duration-200 rounded-xl text-left focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 border border-purple-200 bg-white hover:border-purple-400 px-3.5 py-2 text-xs sm:text-sm h-10 font-semibold cursor-pointer shadow-xs group"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <CalendarIcon className="w-4 h-4 text-purple-600 shrink-0 group-hover:scale-110 transition-transform" />
          <span className="text-gray-900 leading-tight truncate text-xs sm:text-sm font-semibold">
            {!value.start && !value.end
              ? "Selecionar Período"
              : `${value.start?.split("-").reverse().join("/")} — ${value.end?.split("-").reverse().join("/") || "..."}`}
          </span>
        </div>
        {(value.start || value.end) && (
          <X
            className="w-4 h-4 text-gray-400 hover:text-red-500 shrink-0 transition-colors ml-2"
            onClick={(e) => {
              e.stopPropagation();
              onChange({ start: undefined, end: undefined });
            }}
          />
        )}
      </div>

      {isOpen && (
        <div
          className={cn(
            "absolute z-100 bg-white shadow-2xl border border-purple-200 animate-in fade-in zoom-in duration-200 max-w-[calc(100vw-1.5rem)]",
            dropDirection === "up"
              ? variant === "premium"
                ? "bottom-[calc(100%+8px)] right-0 sm:left-0 sm:right-auto rounded-[24px] shadow-[0_50px_100px_rgba(0,0,0,0.15)] p-6 sm:p-8 w-[calc(100vw-1.5rem)] sm:w-[380px]"
                : "bottom-[calc(100%+8px)] right-0 sm:left-0 sm:right-auto rounded-2xl p-4 sm:p-5 w-[calc(100vw-1.5rem)] sm:w-[280px]"
              : variant === "premium"
                ? "top-[calc(100%+8px)] right-0 sm:left-0 sm:right-auto rounded-[24px] shadow-[0_50px_100px_rgba(0,0,0,0.15)] p-6 sm:p-8 w-[calc(100vw-1.5rem)] sm:w-[380px]"
                : "top-[calc(100%+8px)] right-0 sm:left-0 sm:right-auto rounded-2xl p-4 sm:p-5 w-[calc(100vw-1.5rem)] sm:w-[280px]",
          )}
        >
          <div
            className={cn(
              "flex items-center justify-between",
              variant === "premium" ? "mb-8" : "mb-4",
            )}
          >
            <button
              onClick={() =>
                setViewDate(
                  new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1),
                )
              }
              className="p-2 hover:bg-purple-50 rounded-xl text-purple-600 transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <div
              className={cn(
                "font-black text-gray-900 uppercase tracking-widest",
                variant === "premium" ? "text-[13px]" : "text-[11px]",
              )}
            >
              {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
            </div>
            <button
              onClick={() =>
                setViewDate(
                  new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1),
                )
              }
              className="p-2 hover:bg-purple-50 rounded-xl text-purple-600 transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2 text-center">
            {["D", "S", "T", "Q", "Q", "S", "S"].map((d, i) => (
              <div
                key={`${d}-${i}`}
                className="text-[9px] font-black text-gray-300 py-2"
              >
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>

          <div
            className={cn(
              "pt-4 border-t border-gray-50 flex flex-wrap gap-2",
              variant === "premium" ? "mt-6" : "mt-2",
            )}
          >
            <button
              onClick={() => {
                let target = new Date();
                if (startLimitDate && target < startLimitDate)
                  target = new Date(startLimitDate);
                if (limitDate && target > limitDate)
                  target = new Date(limitDate);

                const range = {
                  start: formatDateStr(target),
                  end: formatDateStr(target),
                };
                setTempRange(range);
                onChange(range);
                setIsOpen(false);
              }}
              className="px-3 py-1.5 bg-gray-50 hover:bg-purple-50 text-[10px] font-black text-gray-500 hover:text-purple-600 rounded-xl transition-all border border-transparent hover:border-purple-100"
            >
              Hoje
            </button>
            <button
              onClick={() => {
                const end = new Date();
                let start = new Date();
                start.setDate(start.getDate() - 7);

                if (startLimitDate && start < startLimitDate)
                  start = new Date(startLimitDate);
                if (limitDate && end > limitDate) return; // Se hoje é proibido

                const range = {
                  start: formatDateStr(start),
                  end: formatDateStr(end),
                };
                setTempRange(range);
                onChange(range);
                setIsOpen(false);
              }}
              className="px-3 py-1.5 bg-gray-50 hover:bg-purple-50 text-[10px] font-black text-gray-500 hover:text-purple-600 rounded-xl transition-all border border-transparent hover:border-purple-100"
            >
              Últimos 7 dias
            </button>
            <button
              onClick={() => {
                const now = new Date();
                let start = new Date(now.getFullYear(), now.getMonth(), 1);

                if (startLimitDate && start < startLimitDate)
                  start = new Date(startLimitDate);
                if (startLimitDate && now < startLimitDate) return;

                const range = {
                  start: formatDateStr(start),
                  end: formatDateStr(now),
                };
                setTempRange(range);
                onChange(range);
                setIsOpen(false);
              }}
              className="px-3 py-1.5 bg-gray-50 hover:bg-purple-50 text-[10px] font-black text-gray-500 hover:text-purple-600 rounded-xl transition-all border border-transparent hover:border-purple-100"
            >
              Este Mês
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
